import type {StepCompletionAttemptADto} from '@cohort/admin-schemas/journeyParticipation';
import {useTriggerIntegrations} from '@cohort/merchants/apps/useTriggerIntegrations';
import {SelectPicker} from '@cohort/merchants/components/form/v2/SelectPicker';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

type StepCompletionWrapperProps = {
  children: React.ReactNode;
};

const StepCompletionWrapper: React.FC<StepCompletionWrapperProps> = ({children}) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
    {children}
  </div>
);

type StepCompletionsViewProps = {
  sortedStepCompletionAttempts: Array<StepCompletionAttemptADto>;
};

export const StepCompletionsView: React.FC<StepCompletionsViewProps> = ({
  sortedStepCompletionAttempts,
}) => {
  const [selectedStepCompletionAttempt, setSelectedStepCompletionAttempt] = useState<
    StepCompletionAttemptADto | undefined
  >(sortedStepCompletionAttempts[0]);
  const {getTriggerIntegration} = useTriggerIntegrations();
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.activity.journey.journeyParticipationsStep',
  });

  const triggerIntegration =
    selectedStepCompletionAttempt === undefined ||
    selectedStepCompletionAttempt.triggerIntegrationId === null
      ? undefined
      : getTriggerIntegration(selectedStepCompletionAttempt.triggerIntegrationId);

  if (sortedStepCompletionAttempts.length === 0) {
    throw new Error('Never happens: There is no completion attempt for a failed step');
  }

  if (selectedStepCompletionAttempt === undefined || triggerIntegration === undefined) {
    return <Fragment />;
  }

  if (sortedStepCompletionAttempts.length === 1) {
    return (
      <StepCompletionWrapper>
        <triggerIntegration.stepCompletionComponent data={selectedStepCompletionAttempt.data} />
      </StepCompletionWrapper>
    );
  }

  const selectPickerValue = {
    value: selectedStepCompletionAttempt.createdAt.toISOString(),
    label: t('stepCompletionModal.stepAttemptSelector.label', {
      atttemptNumber: sortedStepCompletionAttempts.indexOf(selectedStepCompletionAttempt) + 1,
    }),
  };
  const selectPickerOptions = sortedStepCompletionAttempts.map((attempt, index) => {
    return {
      value: attempt.createdAt.toISOString(),
      label: t('stepCompletionModal.stepAttemptSelector.label', {
        atttemptNumber: index + 1,
      }),
    };
  });

  return (
    <div className="space-y-4">
      <SelectPicker
        value={selectPickerValue}
        options={selectPickerOptions}
        className="w-1/3"
        name="step completion attempts selector"
        displayMode="overflow"
        onChange={option => {
          if (option !== null) {
            setSelectedStepCompletionAttempt(
              sortedStepCompletionAttempts.find(attempt => {
                return attempt.createdAt.toISOString() === option.value;
              })
            );
          }
        }}
      />
      <StepCompletionWrapper>
        <triggerIntegration.stepCompletionComponent data={selectedStepCompletionAttempt.data} />
      </StepCompletionWrapper>
    </div>
  );
};
